import { requestKarangoApi } from "../../../core/api/karangoTransport";

export async function listAdminUsers({
  cursor,
  limit = 50,
  sortBy = "createdAt",
  sortDir = "desc",
  search,
  filter,
  signal,
} = {}) {
  const params = new URLSearchParams({ limit: String(limit), sortBy, sortDir });
  if (cursor) params.set("cursor", cursor);
  if (search) params.set("search", search);
  if (filter && filter !== "all") params.set("filter", filter);
  const payload = await requestKarangoApi(
    `/v1/admin/users?${params.toString()}`,
    { requiresAuth: true, signal }
  );
  return {
    items:      Array.isArray(payload?.items) ? payload.items : [],
    nextCursor: payload?.nextCursor ?? null,
    total:      payload?.total ?? 0,
  };
}

export async function patchAdminUser(userId, data, { signal } = {}) {
  const payload = await requestKarangoApi(`/v1/admin/users/${encodeURIComponent(userId)}`, {
    requiresAuth: true,
    method: "PATCH",
    body: data,
    signal,
  });
  return payload?.user ?? null;
}

export async function getAdminCertificationOverview({ signal } = {}) {
  const payload = await requestKarangoApi("/v1/admin/certifications/overview", { requiresAuth: true, signal });
  return payload ?? {};
}

export async function listAdminMemberCertificationRequests({ status, signal } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  const payload = await requestKarangoApi(
    `/v1/admin/certifications/member-requests?${params.toString()}`,
    { requiresAuth: true, signal }
  );
  return {
    requests: Array.isArray(payload?.requests) ? payload.requests : [],
    certifiers: Array.isArray(payload?.certifiers) ? payload.certifiers : [],
  };
}

export async function getAdminAnalytics({ signal } = {}) {
  const payload = await requestKarangoApi("/v1/admin/analytics", { requiresAuth: true, signal });
  return payload ?? {};
}

export async function getAdminEventOptionsStats({ signal } = {}) {
  const payload = await requestKarangoApi("/v1/admin/analytics/event-options", { requiresAuth: true, signal });
  return payload ?? {};
}

export async function getAdminAudienceStats({ signal } = {}) {
  const payload = await requestKarangoApi("/v1/admin/analytics/audience", { requiresAuth: true, signal });
  return payload ?? {};
}

export async function getAdminNotificationConfig({ signal } = {}) {
  const payload = await requestKarangoApi(
    "/v1/admin/notifications/config",
    { requiresAuth: true, signal }
  );
  return payload ?? {};
}

export async function saveAdminNotificationPushTemplate(type, values, { signal } = {}) {
  const payload = await requestKarangoApi(
    `/v1/admin/notifications/templates/${encodeURIComponent(type)}`,
    {
      requiresAuth: true,
      method: "PATCH",
      body: values,
      signal,
    }
  );
  return payload?.template ?? null;
}

export async function resetAdminNotificationPushTemplate(type, { signal } = {}) {
  const payload = await requestKarangoApi(
    `/v1/admin/notifications/templates/${encodeURIComponent(type)}`,
    { requiresAuth: true, method: "DELETE", signal }
  );
  return payload?.template ?? null;
}

export async function retryAdminFailedNotificationDeliveries(channel = "PUSH", { signal } = {}) {
  const payload = await requestKarangoApi(
    "/v1/admin/notifications/deliveries/retry",
    {
      requiresAuth: true,
      method: "POST",
      body: { channel },
      signal,
    }
  );
  return payload ?? { channel, queued: 0 };
}

export async function revokeAdminStalePushTokens({ signal } = {}) {
  const payload = await requestKarangoApi(
    "/v1/admin/notifications/push-tokens/revoke-stale",
    { requiresAuth: true, method: "POST", signal }
  );
  return payload ?? { revoked: 0 };
}

export async function getAdminStripeServiceStatus({ signal } = {}) {
  const payload = await requestKarangoApi(
    "/v1/admin/services/stripe",
    { requiresAuth: true, signal }
  );
  return payload ?? {};
}

export async function getAdminDatabaseServiceStatus({ signal } = {}) {
  const payload = await requestKarangoApi(
    "/v1/admin/services/database",
    { requiresAuth: true, signal }
  );
  return payload ?? {};
}

export async function getAdminJobsStatus({ signal } = {}) {
  const payload = await requestKarangoApi(
    "/v1/admin/jobs/status",
    { requiresAuth: true, signal }
  );
  return payload ?? {};
}

export async function listAdminSimulatorEvents({ signal } = {}) {
  const payload = await requestKarangoApi("/v1/admin/events/simulator", { requiresAuth: true, signal });
  return Array.isArray(payload?.events) ? payload.events : [];
}

export async function getAdminEventRoster(eventId, { signal } = {}) {
  const payload = await requestKarangoApi(
    `/v1/admin/events/${encodeURIComponent(eventId)}/roster`,
    { requiresAuth: true, signal }
  );
  return {
    participantIds: Array.isArray(payload?.participantIds) ? payload.participantIds : [],
    pendingIds:     Array.isArray(payload?.pendingIds)     ? payload.pendingIds     : [],
    interestedIds:  Array.isArray(payload?.interestedIds)  ? payload.interestedIds  : [],
    event:          payload?.event ?? null,
  };
}

// Les profils synthetiques agissent par les memes services que les membres :
// le resultat detaille chaque profil (inscrit, en attente, refuse et pourquoi).
export async function adminBulkEnroll(eventId, userIds, action, { accessCode = "", signal } = {}) {
  const body = { userIds, action };
  if (accessCode) body.accessCode = accessCode;
  const payload = await requestKarangoApi(
    `/v1/admin/events/${encodeURIComponent(eventId)}/enroll`,
    { requiresAuth: true, method: "POST", body, signal }
  );
  return payload ?? {};
}

export async function getAdminMessagingStats({ signal } = {}) {
  const payload = await requestKarangoApi(
    "/v1/admin/messaging/stats",
    { requiresAuth: true, signal }
  );
  return payload ?? {};
}

// Mail « Les sorties autour de toi » : agregats par semaine.
export async function getAdminEventsDigestStats({ signal, periods = 8 } = {}) {
  const payload = await requestKarangoApi(
    `/v1/admin/events-digest/stats?periods=${encodeURIComponent(String(periods))}`,
    { requiresAuth: true, signal }
  );
  return payload ?? {};
}

// Reglages du mail des sorties (spec §18).
export async function getAdminEventsDigestSettings({ signal } = {}) {
  return (await requestKarangoApi("/v1/admin/events-digest/settings", { requiresAuth: true, signal })) ?? {};
}

export async function saveAdminEventsDigestSettings({ settings, expectedUpdatedAt }) {
  return (await requestKarangoApi("/v1/admin/events-digest/settings", {
    method: "PUT",
    requiresAuth: true,
    retryable: false,
    body: { settings, expectedUpdatedAt: expectedUpdatedAt || null },
  })) ?? {};
}

// Sorties « A la une » du mail des sorties (spec §23.2).
export async function getAdminEventsDigestFeatures({ signal } = {}) {
  return (await requestKarangoApi("/v1/admin/events-digest/features", { requiresAuth: true, signal })) ?? {};
}

export async function createAdminEventsDigestFeature({ event, reachKm, startsAt = null, endsAt = null }) {
  return (await requestKarangoApi("/v1/admin/events-digest/features", {
    method: "POST",
    requiresAuth: true,
    retryable: false,
    body: { event, reachKm, startsAt, endsAt },
  })) ?? {};
}

export async function stopAdminEventsDigestFeature(featureId) {
  return (await requestKarangoApi(`/v1/admin/events-digest/features/${encodeURIComponent(featureId)}/stop`, {
    method: "POST",
    requiresAuth: true,
    retryable: false,
  })) ?? {};
}

// Envois manuels du mail des sorties (spec §17.2).
export async function getAdminEventsDigestManualContext({ user = "", signal } = {}) {
  const query = user ? `?user=${encodeURIComponent(user)}` : "";
  return (await requestKarangoApi(`/v1/admin/events-digest/manual${query}`, { requiresAuth: true, signal })) ?? {};
}

export async function previewAdminEventsDigest({ user = "" } = {}) {
  return (await requestKarangoApi("/v1/admin/events-digest/preview", { method: "POST", requiresAuth: true, body: { user } })) ?? {};
}

export async function sendAdminEventsDigestTest({ user = "" } = {}) {
  return (await requestKarangoApi("/v1/admin/events-digest/test", { method: "POST", requiresAuth: true, body: { user } })) ?? {};
}

export async function startAdminEventsDigestRun({ scope, user = "", confirmLatestPeriodKey, confirmTargetUserId = null }) {
  return (await requestKarangoApi("/v1/admin/events-digest/runs", {
    method: "POST",
    requiresAuth: true,
    retryable: false,
    body: { scope, user, confirmLatestPeriodKey, confirmScope: scope, confirmTargetUserId },
  })) ?? {};
}

export async function getAdminCampaignsStats({ signal } = {}) {
  const payload = await requestKarangoApi(
    "/v1/admin/campaigns/stats",
    { requiresAuth: true, signal }
  );
  return payload ?? {};
}

export async function bulkCreateProfiles(rows, { signal } = {}) {
  const payload = await requestKarangoApi(
    "/v1/admin/profiles/bulk-create",
    { requiresAuth: true, method: "POST", body: { rows }, signal }
  );
  return payload ?? { processed: 0, errors: 0, results: [] };
}

export async function createSyntheticProfile(row, { signal } = {}) {
  const result = await bulkCreateProfiles([row], { signal });
  const first = result?.results?.[0];
  if (!first?.success) {
    throw new Error(first?.error || "Création du profil impossible.");
  }
  return first.user;
}

export async function bulkCreateEvents(rows, { signal } = {}) {
  const payload = await requestKarangoApi(
    "/v1/admin/events/bulk-create",
    { requiresAuth: true, method: "POST", body: { rows }, signal }
  );
  return payload ?? { processed: 0, errors: 0, results: [] };
}

export async function listAdminReports({ status, cursor, limit = 50, signal } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (status && status !== "all") params.set("status", status);
  if (cursor) params.set("cursor", cursor);
  const payload = await requestKarangoApi(
    `/v1/admin/reports?${params.toString()}`,
    { requiresAuth: true, signal }
  );
  return {
    reports:    Array.isArray(payload?.reports) ? payload.reports : [],
    nextCursor: payload?.nextCursor ?? null,
  };
}

export async function patchAdminReport(reportId, { status, resolution } = {}, { signal } = {}) {
  const payload = await requestKarangoApi(
    `/v1/admin/reports/${encodeURIComponent(reportId)}`,
    { requiresAuth: true, method: "PATCH", body: { status, resolution }, signal }
  );
  return payload?.report ?? null;
}

export async function listAdminPhotos({ status, cursor, limit = 50, signal } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (status && status !== "all") params.set("status", status);
  if (cursor) params.set("cursor", cursor);
  const payload = await requestKarangoApi(
    `/v1/admin/photos?${params.toString()}`,
    { requiresAuth: true, signal }
  );
  return {
    photos:     Array.isArray(payload?.photos) ? payload.photos : [],
    nextCursor: payload?.nextCursor ?? null,
  };
}

export async function patchAdminPhoto(photoId, { status, reason } = {}, { signal } = {}) {
  const payload = await requestKarangoApi(
    `/v1/admin/photos/${encodeURIComponent(photoId)}`,
    { requiresAuth: true, method: "PATCH", body: { status, reason }, signal }
  );
  return payload?.photo ?? null;
}

export async function listAdminModerationActions({ action, targetType, cursor, limit = 50, signal } = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (action && action !== "all") params.set("action", action);
  if (targetType && targetType !== "all") params.set("targetType", targetType);
  if (cursor) params.set("cursor", cursor);
  const payload = await requestKarangoApi(
    `/v1/admin/moderation-actions?${params.toString()}`,
    { requiresAuth: true, signal }
  );
  return {
    actions:    Array.isArray(payload?.actions) ? payload.actions : [],
    nextCursor: payload?.nextCursor ?? null,
  };
}

export async function listSyntheticUsers({ signal } = {}) {
  const payload = await requestKarangoApi("/v1/admin/synthetic-users", { requiresAuth: true, signal });
  return Array.isArray(payload?.users) ? payload.users : [];
}

export async function impersonateSyntheticUser(userId, { signal } = {}) {
  const payload = await requestKarangoApi("/v1/admin/impersonate", {
    requiresAuth: true,
    method: "POST",
    body: { userId },
    signal,
  });
  return payload?.customToken ?? null;
}

// Sorties a venir des membres (compte « karango » exclu) pour le texte Facebook.
export async function getAdminSocialExtract({ days = 14, signal } = {}) {
  const payload = await requestKarangoApi(
    `/v1/admin/events/social-extract?days=${encodeURIComponent(String(days))}`,
    { requiresAuth: true, signal }
  );
  return {
    days: Number(payload?.days) || days,
    events: Array.isArray(payload?.events) ? payload.events : [],
  };
}

